import React from 'react';
import styled, { keyframes } from 'styled-components';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { getSiteProductKind, isStaysSite } from '../../../constants/siteConfig';
import { claimDetailPath } from '../../../utils/productRoutes';
import { useSnapshot } from 'valtio';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faChevronRight,
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { useGetMyDamageClaimsQuery } from '../../../services/damageClaimService/__generated__/queries.generated';
import { authStore } from '../../../../store/authStore';
import { DamageClaimStatus, DamageType, ProductType } from '../../../../__generated__/graphql';
import { getShortIdFromUuid, formatShortId } from '../../../utils/short-id';

const PageContainer = styled.div`
  ${tw`flex flex-col w-full max-w-full min-w-0 h-full max-w-screen-xl mx-auto pb-10 md:pb-16 box-border`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-4 px-4 lg:px-12 mt-6 md:mt-8 mb-6`}
`;

const BackButton = styled.button`
  ${tw`p-2 text-text-secondary hover:text-text-secondary rounded-full hover:bg-bg-secondary`}
`;

const Title = styled.h1`
  ${tw`text-3xl font-bold text-text-primary`}
`;

const EmptyState = styled.div`
  ${tw`w-full text-center text-text-secondary mt-20 px-4`}
`;

const EmptyIcon = styled.div`
  ${tw`text-6xl text-text-tertiary mb-4`}
`;

const ClaimsList = styled.div`
  ${tw`flex flex-col gap-4 px-4 lg:px-12`}
`;

const ClaimCard = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default p-6  transition-shadow cursor-pointer`}
  &:hover {
    ${tw`shadow-card`}
  }
`;

const ClaimHeader = styled.div`
  ${tw`flex items-start justify-between mb-4`}
`;

const ClaimInfo = styled.div`
  ${tw`flex-1`}
`;

const ClaimNumber = styled.h3`
  ${tw`text-lg font-semibold text-text-primary`}
`;

const ClaimDate = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const StatusBadge = styled.span<{ status: DamageClaimStatus }>`
  ${tw`px-3 py-1 rounded-full text-sm font-medium max-w-full min-w-0 box-border`}
  ${props => {
    switch (props.status) {
      case DamageClaimStatus.Pending:
        return tw`bg-yellow-100 text-yellow-800`;
      case DamageClaimStatus.UnderReview:
        return tw`bg-blue-100 text-blue-800`;
      case DamageClaimStatus.Approved:
        return tw`bg-green-100 text-green-800`;
      case DamageClaimStatus.PartiallyApproved:
        return tw`bg-green-100 text-green-700`;
      case DamageClaimStatus.Rejected:
        return tw`bg-red-100 text-red-800`;
      case DamageClaimStatus.Resolved:
        return tw`bg-bg-secondary text-text-primary`;
      case DamageClaimStatus.Withdrawn:
        return tw`bg-bg-secondary text-text-secondary`;
      default:
        return tw`bg-bg-secondary text-text-primary`;
    }
  }}
`;

const BadgesContainer = styled.div`
  ${tw`flex flex-col items-end gap-2`}
`;

const ResponseBadge = styled.span<{ reviewed: boolean }>`
  ${tw`px-3 py-1 rounded-full text-xs font-medium`}
  ${props => (props.reviewed ? tw`bg-blue-100 text-black` : tw`bg-orange-100 text-orange-700`)}
`;

const ReporterBadge = styled.span<{ isHost: boolean }>`
  ${tw`px-2 py-0.5 rounded-md text-xs font-medium`}
  ${props =>
    props.isHost ? tw`bg-purple-100 text-black` : tw`bg-bg-secondary text-text-secondary`}
`;

const ShortIdBadge = styled.span`
  ${tw`bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center uppercase ml-2`}
`;

// Skeleton Loading Styles
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

const SkeletonCard = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default p-6 mb-4`}
`;

const SkeletonHeader = styled.div`
  ${tw`flex items-start justify-between mb-4`}
`;

const SkeletonTitle = styled(SkeletonBase)`
  ${tw`h-6 w-40 mb-2`}
`;

const SkeletonDate = styled(SkeletonBase)`
  ${tw`h-4 w-32`}
`;

const SkeletonBadge = styled(SkeletonBase)`
  ${tw`h-8 w-28 rounded-full`}
`;

const SkeletonBadgeSmall = styled(SkeletonBase)`
  ${tw`h-6 w-36 rounded-full mt-2`}
`;

const SkeletonDetails = styled.div`
  ${tw`flex gap-4 mb-4`}
`;

const SkeletonDetailItem = styled(SkeletonBase)`
  ${tw`h-4 w-24`}
`;

const SkeletonCarInfo = styled.div`
  ${tw`flex items-center gap-3 mt-4 pt-4 border-t border-border-default`}
`;

const SkeletonThumbnail = styled(SkeletonBase)`
  ${tw`w-16 h-12 rounded-lg`}
`;

const SkeletonCarName = styled(SkeletonBase)`
  ${tw`h-5 w-40 mb-2`}
`;

const SkeletonTripDate = styled(SkeletonBase)`
  ${tw`h-4 w-28`}
`;

const ClaimCardSkeleton: React.FC = () => (
  <SkeletonCard>
    <SkeletonHeader>
      <div>
        <SkeletonTitle />
        <SkeletonDate />
      </div>
      <div className="flex flex-col items-end">
        <SkeletonBadge />
        <SkeletonBadgeSmall />
      </div>
    </SkeletonHeader>
    <SkeletonDetails>
      <SkeletonDetailItem />
      <SkeletonDetailItem />
      <SkeletonDetailItem />
    </SkeletonDetails>
    <SkeletonCarInfo>
      <SkeletonThumbnail />
      <div className="flex-1">
        <SkeletonCarName />
        <SkeletonTripDate />
      </div>
    </SkeletonCarInfo>
  </SkeletonCard>
);

const ClaimsListSkeleton: React.FC = () => (
  <ClaimsList>
    <ClaimCardSkeleton />
    <ClaimCardSkeleton />
    <ClaimCardSkeleton />
  </ClaimsList>
);

const ClaimDetails = styled.div`
  ${tw`flex flex-wrap gap-4 text-sm text-text-secondary`}
`;

const DetailItem = styled.div`
  ${tw`flex items-center gap-2`}
`;

const DetailLabel = styled.span`
  ${tw`text-text-tertiary`}
`;

const ChevronIcon = styled.div`
  ${tw`text-text-tertiary`}
`;

const CarInfo = styled.div`
  ${tw`flex items-center gap-3 mt-4 pt-4 border-t border-border-default`}
`;

const CarThumbnail = styled.img`
  ${tw`w-16 h-12 object-cover rounded-lg`}
`;

const CarDetails = styled.div`
  ${tw`flex-1`}
`;

const CarName = styled.p`
  ${tw`font-medium text-text-primary`}
`;

const TripDates = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const getStatusLabel = (status: DamageClaimStatus): string => {
  switch (status) {
    case DamageClaimStatus.Pending:
      return 'Pending';
    case DamageClaimStatus.UnderReview:
      return 'Under Review';
    case DamageClaimStatus.Approved:
      return 'Approved';
    case DamageClaimStatus.PartiallyApproved:
      return 'Partially Approved';
    case DamageClaimStatus.Rejected:
      return 'Rejected';
    case DamageClaimStatus.Resolved:
      return 'Resolved';
    case DamageClaimStatus.Withdrawn:
      return 'Withdrawn';
    default:
      return status;
  }
};

const getDamageTypeLabel = (type: DamageType): string => {
  switch (type) {
    case DamageType.Physical:
      return 'Physical Damage';
    case DamageType.Mechanical:
      return 'Mechanical Issue';
    default:
      return type;
  }
};

const formatDate = (dateValue: string | number | Date): string => {
  // Handle different date formats: ISO string, timestamp string, or number
  let date: Date;
  if (typeof dateValue === 'string') {
    // Check if it's a numeric timestamp string or ISO date string
    const parsed = parseInt(dateValue);
    if (!isNaN(parsed) && String(parsed) === dateValue) {
      // It's a numeric timestamp string
      date = new Date(parsed);
    } else {
      // It's an ISO date string
      date = new Date(dateValue);
    }
  } else {
    date = new Date(dateValue);
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatTripDates = (startAt: string, endAt: string): string => {
  const start = new Date(parseInt(startAt));
  const end = new Date(parseInt(endAt));
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

const DamageClaimsPage: React.FC = () => {
  const navigate = useNavigate();
  // Spec 016 W25: on the stays site claims are Resolution Center requests.
  const stays = isStaysSite();
  const { isAuthenticated } = useSnapshot(authStore);

  const { data, loading, error } = useGetMyDamageClaimsQuery({
    skip: !isAuthenticated,
  });

  const claims = data?.myDamageClaims || [];

  const handleClaimClick = (claimId: string) => {
    navigate(claimDetailPath('host', claimId, getSiteProductKind()));
  };

  if (!isAuthenticated) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </BackButton>
        <Title>{stays ? 'Resolution Center' : 'My Damage Claims'}</Title>
      </Header>

      {loading && <ClaimsListSkeleton />}

      {error && (
        <EmptyState>
          <p>Error loading claims: {error.message}</p>
        </EmptyState>
      )}

      {!loading && !error && claims.length === 0 && (
        <EmptyState>
          <EmptyIcon>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </EmptyIcon>
          <p className="text-lg font-medium text-gray-700 mb-2">
            {stays ? 'No requests' : 'No damage claims'}
          </p>
          <p>You haven't submitted any damage claims yet.</p>
        </EmptyState>
      )}

      {!loading && !error && claims.length > 0 && (
        <ClaimsList>
          {claims.map((claim: any) => {
            const isHostReporter = claim.reporterId === claim.hostId;
            return (
              <ClaimCard key={claim.id} onClick={() => handleClaimClick(claim.id)}>
                <ClaimHeader>
                  <ClaimInfo>
                    <div className="flex items-center gap-2 mb-1">
                      <ClaimNumber>
                        {stays ? 'Request' : 'Claim'} #{claim.claimNum}
                      </ClaimNumber>
                      <ReporterBadge isHost={isHostReporter}>
                        {isHostReporter ? 'You reported' : 'Guest reported'}
                      </ReporterBadge>
                      <ShortIdBadge>
                        ID: {formatShortId(claim.shortId || getShortIdFromUuid(claim.id))}
                      </ShortIdBadge>
                    </div>
                    <ClaimDate>Submitted {formatDate(claim.createdAt)}</ClaimDate>
                  </ClaimInfo>
                  <BadgesContainer>
                    <StatusBadge status={claim.status}>{getStatusLabel(claim.status)}</StatusBadge>
                    <ResponseBadge reviewed={claim.counterpartyReviewed}>
                      {claim.counterpartyReviewed ? 'Counterparty Responded' : 'Awaiting Response'}
                    </ResponseBadge>
                  </BadgesContainer>
                </ClaimHeader>

                <ClaimDetails>
                  <DetailItem>
                    <DetailLabel>Type:</DetailLabel>
                    <span>{getDamageTypeLabel(claim.damageType)}</span>
                  </DetailItem>
                  {claim.repairEstimateRange && (
                    <DetailItem>
                      <DetailLabel>Estimate:</DetailLabel>
                      <span>{claim.repairEstimateRange.replace(/_/g, ' ').toLowerCase()}</span>
                    </DetailItem>
                  )}
                  {claim.approvedAmount && (
                    <DetailItem>
                      <DetailLabel>Approved:</DetailLabel>
                      <span className="text-green-600 font-medium">
                        ${claim.approvedAmount.toFixed(2)}
                      </span>
                    </DetailItem>
                  )}
                </ClaimDetails>

                {claim.booking && (
                  <CarInfo>
                    {claim.booking.car?.thumbnailUrl && (
                      <CarThumbnail
                        src={claim.booking.car.thumbnailUrl}
                        alt={claim.booking.car.name || 'Listing photo'}
                      />
                    )}
                    <CarDetails>
                      <CarName>
                        {claim.booking.productType === ProductType.House
                          ? claim.booking.car?.name || 'Place to stay'
                          : `${claim.booking.car?.year ?? ''} ${claim.booking.car?.make ?? ''} ${
                              claim.booking.car?.model ?? ''
                            }`.trim()}
                      </CarName>
                      <TripDates>
                        {claim.booking.productType === ProductType.House ? 'Stay' : 'Trip'}:{' '}
                        {formatTripDates(claim.booking.startAt, claim.booking.endAt)}
                      </TripDates>
                    </CarDetails>
                    <ChevronIcon>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </ChevronIcon>
                  </CarInfo>
                )}
              </ClaimCard>
            );
          })}
        </ClaimsList>
      )}
    </PageContainer>
  );
};

export default DamageClaimsPage;
